import {
	AfterViewInit,
	ChangeDetectorRef,
	Directive,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren
} from '@angular/core';
import {USER_PERMISSION_SERVICE} from '@im-ng/token/user';
import {SelfControlService} from '../services/self-control.service';
import type {UserPermissionService} from '@im-ng/user';
import {DotCoreService} from '@ng-dot/core';
import {DotUiService} from '@ng-dot/ui';
import {DotDualistComponent} from '@ng-dot/ui/dualist';
import {BehaviorSubject, tap} from 'rxjs';
import {PageList} from '../typings/page-list';
import {MainListComponentBase} from './main-list-component-base';

@Directive()
/**
 * SelfControlService 와 연동되는 **다중** CRUD 조작 컴포넌트 기반
 *
 * @see SelfControlService
 * @see MainComponentBase
 * @see MainListComponentBase
 * @see PageMagicComponent
 */
export abstract class ControlComponentBase<T = any, U = T extends (infer V)[] ? V : T> extends MainListComponentBase<T>
	implements OnInit, AfterViewInit, OnDestroy, PageList {

	protected _dotUi: DotUiService;
	protected _controlInit!: boolean;
	protected _permission: UserPermissionService;
	protected _control: SelfControlService;
	protected _change: ChangeDetectorRef
	multiSelected$           = new BehaviorSubject<number>(0);
	_multiSelectedItems: U[] = [];

	protected _selfAfterDeletes = this._controlAfterDeletes.bind(this);

	selectedItem$ = new BehaviorSubject<U>(null as any);

	@ViewChild(DotDualistComponent, {static: false}) listRef: DotDualistComponent;

	@ViewChildren(DotDualistComponent) listRefs: QueryList<DotDualistComponent>;

	protected constructor(
		feature?: string
	) {
		super(feature);

		const {_injector} = this;
		this._dotUi       = _injector.get(DotUiService);
		this._control     = _injector.get(SelfControlService);
		this._change      = _injector.get(ChangeDetectorRef)
		const _core       = _injector.get(DotCoreService);
		if (_core.includes(USER_PERMISSION_SERVICE)) {
			this._permission = _core.inject(USER_PERMISSION_SERVICE);
		}

		if (feature) {
			if (this._translate.isDefineId(feature)) {
				this._setDefinedHeading(feature);
			} else {
				this._setHeading(feature);
			}
			this._control.setFeature(this.feature);
		}
	}

	#permit(...requireConditions) {
		return this._permission?.permit(...requireConditions) || true;
	}


	override ngOnInit() {
		this.controlOnInit();
		this._controlInit = true;
		super.ngOnInit();


		if (this['listAdjusts'] && (!this['listCheckbox'] && this['listCheckbox'] !== false)) {
			const permissionPrefix = this._control._permissionPrefix;
			if (!this._permission || !permissionPrefix || this.#permit(permissionPrefix + '/write')) {
				this['listCheckbox'] = true;
			}
		}
	}

	ngAfterViewInit() {
		this.#setListRef();

		const {listRefs} = this;
		this.listRef     = listRefs.first;

		listRefs.changes.subscribe(refs => {
			this._change.detectChanges()
			this.#setListRef();
		});
	}


	override ngOnDestroy() {
		this.controlOnDestroy();
		super.ngOnDestroy();
		this._destroy$.next();

		if (this.listRef) {

			this._dotUi.popRemoteRef('datatable');
		}
	}

	#setListRef() {
		this.listRef = this.listRefs.first;
		this._dotUi.setRemoteRef('datatable', this.listRef);

	}

	/**
	 * 생성
	 */
	create() {
		this._control.create();
	}

	/**
	 * 수정
	 */
	update(item: U, params?) {
		this._control.update$(item, params).subscribe();
	}

	/**
	 * 삭제
	 * @deprecated delete$() 사용
	 */
	delete(item: U, target?: string) {
		this._control.delete$(item as any, target)
			.subscribe();
	}

	/**
	 * 삭제
	 * @param item
	 * @param target
	 */
	delete$(item: U, target?: string) {
		return this._control.delete$(item as any, target);
	}


	/**
	 * 다중 삭제
	 */
	deletes() {
		this._deletesDialog(this.listRef.checkMulti$.value as any);
	}


	/**
	 * 비동기 다중 삭제
	 */
	deletes$() {
		return this._control.deletes$(this.listRef.checkMulti$.value as any)
			.pipe(tap(this._selfAfterDeletes));
	}

	/**
	 * 전체 삭제
	 */
	deleteAll() {
		this._control.deleteAll();
	}

	protected _deletesDialog(items: U[], target?: string) {
		this._control.deletes(items as any, target);
	}

	selected(item: U) {
		this.selectedItem$.next(item);
	}

	multiSelected(items: U[]) {
		const {length}                        = items;
		const {selectedItem$, multiSelected$} = this;
		if (length) {
			if (length > 1) {
				multiSelected$.next(length);
				this._multiSelectedItems = items;
			} else {
				multiSelected$.next(0);
				this._multiSelectedItems = items;
			}
			selectedItem$.next(items[0]);
		} else {
			selectedItem$.next(null as any);
			multiSelected$.next(0);
			this._multiSelectedItems = [];
		}
	}

	changed() {
		this.selectedItem$.next(null as any);
		this.multiSelected$.next(0);
		this._multiSelectedItems = [];

		this._control.changed();
	}

	restore() {
		this._control.restore();
	}

	/**
	 * @deprecated super.ngOnInit()과 함께 ngOnInit() 사용
	 */
	protected controlOnInit() {
	}

	/**
	 * @deprecated super.ngOnDestroy()와 함께 ngOnDestroy() 사용
	 */
	protected controlOnDestroy() {

	}

	/**
	 * 다중 삭제 성공 후 실행
	 * @protected
	 */
	protected _controlAfterDeletes() {
	}
}
